
import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useProfileCompleteness } from "@/hooks/useProfileCompleteness";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigation } from "@/hooks/useNavigation";

interface ProfileCheckContextType {
  checkProfileBeforeBooking: () => boolean;
  redirectToProfile: (reason?: string) => void;
}

const ProfileCheckContext = createContext<ProfileCheckContextType | undefined>(undefined);

export const useProfileCheck = () => {
  const context = useContext(ProfileCheckContext);
  if (!context) {
    throw new Error("useProfileCheck must be used within a ProfileCheckProvider");
  }
  return context;
};

interface ProfileCheckProviderProps {
  children: ReactNode;
}

export const ProfileCheckProvider: React.FC<ProfileCheckProviderProps> = ({ children }) => {
  const { isComplete, isLoading, updateCompletionStatus } = useProfileCompleteness();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const location = useLocation();
  const { toast } = useToast();
  const { saveCurrentPath, navigateToProfile } = useNavigation();

  // Check if we're already on the profile page to avoid redirect loops
  const isProfilePage = location.pathname === "/my-profile";
  
  // Store the original location when redirecting
  useEffect(() => {
    if (!isProfilePage && isAuthenticated && !authLoading) {
      saveCurrentPath();
    }
  }, [location.pathname, isAuthenticated, authLoading, isProfilePage, saveCurrentPath]);

  // Wait for loading to complete before making any redirections
  useEffect(() => {
    if (!authLoading && isAuthenticated && !isLoading) {
      setInitialCheckDone(true);
    }
  }, [authLoading, isAuthenticated, isLoading]);

  const checkProfileBeforeBooking = () => {
    console.log(`[ProfileCheck] checkProfileBeforeBooking called - isAuthenticated: ${isAuthenticated}, isLoading: ${isLoading}, isComplete: ${isComplete}`);
    
    if (!isAuthenticated) return false;
    
    // If profile completeness is still loading, don't proceed with booking but don't show error
    if (isLoading) {
      console.log('[ProfileCheck] Profile completeness still loading, preventing booking without error message');
      return false;
    }
    
    if (!isComplete) {
      console.log(`[ProfileCheck] Profile incomplete`);
      
      toast({
        title: "Profile Incomplete",
        description: "Please complete your phone number and address to book services.",
        duration: 4000,
      });
      return false;
    }
    
    console.log('[ProfileCheck] Profile is complete, allowing booking');
    return true;
  };

  const redirectToProfile = (reason = 'booking') => {
    console.log(`[ProfileCheck] Redirecting to profile with reason: ${reason}, timestamp: ${new Date().toISOString()}`);
    saveCurrentPath();
    
    // Redirect to profile page with edit mode indicator and reason
    navigateToProfile({ 
      state: { 
        edit: true, 
        reason 
      }
    });
  };

  return (
    <ProfileCheckContext.Provider
      value={{
        checkProfileBeforeBooking,
        redirectToProfile
      }}
    >
      {children}
    </ProfileCheckContext.Provider>
  );
};
